import { useState, useEffect } from 'react';
import { Plus, Upload, Search, BookUser, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFetchContacts } from '@/hooksApi/useContact';
import { useContactsStore } from '@/stores/contactStore';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { SettingsListLoading } from '@/components/SettingsListLoading';
import { ROUTE } from '@/config';
import { ContactRow } from './ContactRow';
import { CsvImportModal } from './CsvImportModal';

const PAGE_SIZE = 20;

const SettingsAddressBook = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Store state
  const searchQuery = useContactsStore((s) => s.searchQuery);
  const setSearchQuery = useContactsStore((s) => s.setSearchQuery);
  const currentPage = useContactsStore((s) => s.currentPage);
  const setCurrentPage = useContactsStore((s) => s.setCurrentPage);

  // Local search input with 300ms debounce
  const [localSearch, setLocalSearch] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localSearch);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, setSearchQuery, setCurrentPage]);

  // CSV import modal
  const [showImport, setShowImport] = useState(false);

  // Data
  const { data, isLoading } = useFetchContacts(
    currentPage,
    PAGE_SIZE,
    searchQuery || undefined,
  );
  const contacts = data?.object?.items ?? [];
  const totalPages = data?.object?.pages ?? 0;
  const totalItems = data?.object?.total ?? 0;

  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <SettingsBreadcrumb moduleName='address_book' moduleRoute={ROUTE.SETTINGS_ADDRESS_BOOK} />

        {/* Header */}
        <div className='flex items-center justify-between'>
          <h1 className='text-xl font-semibold text-sd-foreground m-0'>
            {String(t('address_book'))}
          </h1>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => setShowImport(true)}
              className='flex items-center gap-2 px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              <Upload size={16} />
              {String(t('import_from_csv'))}
            </button>
            <button
              onClick={() => navigate('/settings/address-book/new')}
              className='flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
            >
              <Plus className='h-4 w-4' />
              {String(t('add_contact'))}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className='relative'>
          <Search className='absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sd-muted-foreground pointer-events-none' />
          <input
            type='text'
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder={String(t('search_contacts'))}
            aria-label={String(t('search_contacts'))}
            className='w-full h-10 ps-9 pe-8 rounded-lg border border-sd-input bg-sd-input-bg text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring'
          />
          {localSearch && (
            <button
              onClick={() => setLocalSearch('')}
              aria-label={String(t('clear_search'))}
              className='absolute end-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-sd-muted-foreground hover:text-sd-foreground transition-colors'
            >
              <X className='h-3.5 w-3.5' />
            </button>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <SettingsListLoading />
        ) : contacts.length === 0 ? (
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-10 flex flex-col items-center text-center gap-3'>
            <div className='h-12 w-12 rounded-xl bg-sd-muted flex items-center justify-center'>
              <BookUser className='h-6 w-6 text-sd-muted-foreground' />
            </div>
            <div className='space-y-1'>
              <p className='text-sm font-medium text-sd-foreground'>
                {searchQuery
                  ? String(t('no_contacts_match', { query: searchQuery }))
                  : String(t('no_contacts_yet'))}
              </p>
              <p className='text-xs text-sd-muted-foreground'>
                {searchQuery
                  ? String(t('no_contacts_match_description'))
                  : String(t('no_contacts_description'))}
              </p>
            </div>
            {searchQuery ? (
              <button
                onClick={() => setLocalSearch('')}
                className='mt-1 px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
              >
                {String(t('clear_search'))}
              </button>
            ) : (
              <button
                onClick={() => navigate('/settings/address-book/new')}
                className='mt-1 flex items-center gap-2 px-4 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
              >
                <Plus className='h-4 w-4' />
                {String(t('add_contact'))}
              </button>
            )}
          </div>
        ) : (
          <div className='space-y-3'>
            {contacts.map((contact) => (
              <ContactRow
                key={contact.uid}
                contact={contact}
                onEdit={(uid) => navigate(`/settings/address-book/${uid}/edit`)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex items-center justify-center gap-4'>
            <button
              disabled={!hasPrev}
              onClick={() => setCurrentPage(currentPage - 1)}
              className='flex items-center gap-1 px-3 py-1.5 rounded-lg border border-sd-input bg-sd-background text-xs font-medium hover:bg-sd-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <ChevronLeft size={14} className='rtl:rotate-180' />
              {String(t('previous'))}
            </button>
            <span className='text-xs text-sd-muted-foreground tabular-nums'>
              {totalItems > 0
                ? `${startItem}–${endItem} / ${totalItems}`
                : `${currentPage} / ${totalPages}`}
            </span>
            <button
              disabled={!hasNext}
              onClick={() => setCurrentPage(currentPage + 1)}
              className='flex items-center gap-1 px-3 py-1.5 rounded-lg border border-sd-input bg-sd-background text-xs font-medium hover:bg-sd-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {String(t('next'))}
              <ChevronRight size={14} className='rtl:rotate-180' />
            </button>
          </div>
        )}
      </div>

      {/* CSV Import Modal */}
      <CsvImportModal open={showImport} onClose={() => setShowImport(false)} />
    </div>
  );
};

export default SettingsAddressBook;
